import { useEffect } from "react";
import { useLocalSearchParams } from "expo-router";
import { useFleetActivation } from "@visioni/a3s-monitoring/app";
import { useReplaceTenantRoute } from "@src/features/tenants";
import { activationHref } from "../routes";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { ActivationDetailView } from "../components/ActivationDetailView";
import { ActivationStatus } from "../components/ActivationStatus";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { EMPTY, formatInstant, hasText } from "../format";

const rawId = (id: string) => id;

/**
 * One activation by ticket alone, for one whose tenant the directory does not
 * hold, or that has none. A ticket whose tenant it does hold is redirected to
 * that tenant's own page.
 */
export function FleetActivationScreen() {
  const replaceRoute = useReplaceTenantRoute();
  const { tenantId, ticketNo = "" } = useLocalSearchParams<{ tenantId: string; ticketNo?: string }>();
  const { activation, isLoading, error, refetch } = useFleetActivation(ticketNo);
  const tenantName = useDirectoryTenantName();

  const owner = activation?.a3sTenant;
  const known = owner !== undefined && tenantName(owner) !== undefined ? owner : undefined;

  useEffect(() => {
    if (known !== undefined) replaceRoute(activationHref(tenantId, ticketNo, known));
  }, [known, tenantId, ticketNo, replaceRoute]);

  return (
    <PatriotToolsPage
      title={`Activation ${ticketNo}`}
      {...(activation !== undefined && {
        subtitle: [activation.description, `created ${formatInstant(activation.createdDateTime)}`]
          .filter(hasText)
          .join(" · "),
        titleAccessory: <ActivationStatus activation={activation} />,
      })}
    >
      {activation === undefined || known !== undefined ?
        <QueryNotice isLoading={isLoading || known !== undefined} error={error} onRetry={refetch} />
      : <ActivationDetailView
          activation={activation}
          tenant={{ label: owner ?? EMPTY }}
          siteLabel={rawId}
          cameraLabel={rawId}
        />
      }
    </PatriotToolsPage>
  );
}
